/**
 * Document Enhancement Utilities
 * Canvas-based image processing for document cleanup and enhancement
 */

/**
 * Applies brightness and contrast adjustments to image data
 */
export function adjustBrightnessContrast(imageData, brightness = 0, contrast = 0) {
  const data = imageData.data
  const factor = (259 * (contrast + 255)) / (255 * (259 - contrast))

  for (let i = 0; i < data.length; i += 4) {
    data[i] = clamp(factor * (data[i] - 128) + 128 + brightness)     // R
    data[i + 1] = clamp(factor * (data[i + 1] - 128) + 128 + brightness) // G
    data[i + 2] = clamp(factor * (data[i + 2] - 128) + 128 + brightness) // B
  }

  return imageData
}

/**
 * Converts image to grayscale
 */
export function toGrayscale(imageData) {
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
    data[i] = gray     // R
    data[i + 1] = gray // G
    data[i + 2] = gray // B
  }

  return imageData
}

/**
 * Converts image to black and white (binary threshold)
 */
export function toBlackWhite(imageData, threshold = 128) {
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
    const bw = gray > threshold ? 255 : 0
    data[i] = bw     // R
    data[i + 1] = bw // G
    data[i + 2] = bw // B
  }

  return imageData
}

/**
 * Applies Otsu's threshold for optimal B&W conversion
 */
export function toBlackWhiteOtsu(imageData) {
  const data = imageData.data
  const histogram = new Array(256).fill(0)

  // Build histogram
  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2])
    histogram[gray]++
  }

  // Calculate Otsu threshold
  const total = data.length / 4
  let sum = 0
  for (let i = 0; i < 256; i++) sum += i * histogram[i]

  let sumB = 0
  let wB = 0
  let wF = 0
  let maxVariance = 0
  let threshold = 0

  for (let i = 0; i < 256; i++) {
    wB += histogram[i]
    if (wB === 0) continue
    wF = total - wB
    if (wF === 0) break

    sumB += i * histogram[i]
    const mB = sumB / wB
    const mF = (sum - sumB) / wF
    const variance = wB * wF * (mB - mF) * (mB - mF)

    if (variance > maxVariance) {
      maxVariance = variance
      threshold = i
    }
  }

  return toBlackWhite(imageData, threshold)
}

/**
 * Sharpens the image for clearer text
 */
export function sharpen(imageData, amount = 1) {
  const data = imageData.data
  const width = imageData.width
  const height = imageData.height
  const output = new Uint8ClampedArray(data)

  // Sharpening kernel
  const kernel = [
    0, -amount, 0,
    -amount, 1 + 4 * amount, -amount,
    0, -amount, 0
  ]

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      for (let c = 0; c < 3; c++) {
        let sum = 0
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4 + c
            sum += data[idx] * kernel[(ky + 1) * 3 + (kx + 1)]
          }
        }
        output[(y * width + x) * 4 + c] = clamp(sum)
      }
    }
  }

  for (let i = 0; i < data.length; i++) {
    data[i] = output[i]
  }

  return imageData
}

/**
 * Auto-enhances document for better readability
 * Applies contrast stretch and slight sharpening
 */
export function autoEnhance(imageData) {
  const data = imageData.data

  // Find min/max for contrast stretching
  let min = 255, max = 0
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
    if (gray < min) min = gray
    if (gray > max) max = gray
  }

  // Apply contrast stretch
  const range = max - min || 1
  for (let i = 0; i < data.length; i += 4) {
    data[i] = clamp(((data[i] - min) / range) * 255)
    data[i + 1] = clamp(((data[i + 1] - min) / range) * 255)
    data[i + 2] = clamp(((data[i + 2] - min) / range) * 255)
  }

  // Apply light sharpening
  return sharpen(imageData, 0.5)
}

/**
 * Inverts colors (useful for dark documents)
 */
export function invert(imageData) {
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - data[i]
    data[i + 1] = 255 - data[i + 1]
    data[i + 2] = 255 - data[i + 2]
  }

  return imageData
}

/**
 * Removes noise using median filter
 */
export function denoise(imageData) {
  const data = imageData.data
  const width = imageData.width
  const height = imageData.height
  const output = new Uint8ClampedArray(data)

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      for (let c = 0; c < 3; c++) {
        const neighbors = []
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            neighbors.push(data[((y + ky) * width + (x + kx)) * 4 + c])
          }
        }
        neighbors.sort((a, b) => a - b)
        output[(y * width + x) * 4 + c] = neighbors[4] // Median
      }
    }
  }

  for (let i = 0; i < data.length; i++) {
    data[i] = output[i]
  }

  return imageData
}

/**
 * Rotates image by specified degrees
 */
export function rotateImage(canvas, degrees) {
  const ctx = canvas.getContext('2d')
  const tempCanvas = document.createElement('canvas')
  const tempCtx = tempCanvas.getContext('2d')

  const radians = (degrees * Math.PI) / 180

  if (degrees === 90 || degrees === -270) {
    tempCanvas.width = canvas.height
    tempCanvas.height = canvas.width
    tempCtx.translate(tempCanvas.width, 0)
    tempCtx.rotate(Math.PI / 2)
  } else if (degrees === -90 || degrees === 270) {
    tempCanvas.width = canvas.height
    tempCanvas.height = canvas.width
    tempCtx.translate(0, tempCanvas.height)
    tempCtx.rotate(-Math.PI / 2)
  } else if (degrees === 180 || degrees === -180) {
    tempCanvas.width = canvas.width
    tempCanvas.height = canvas.height
    tempCtx.translate(tempCanvas.width, tempCanvas.height)
    tempCtx.rotate(Math.PI)
  } else {
    // Arbitrary rotation
    const cos = Math.abs(Math.cos(radians))
    const sin = Math.abs(Math.sin(radians))
    tempCanvas.width = canvas.width * cos + canvas.height * sin
    tempCanvas.height = canvas.width * sin + canvas.height * cos
    tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2)
    tempCtx.rotate(radians)
    tempCtx.translate(-canvas.width / 2, -canvas.height / 2)
  }

  tempCtx.drawImage(canvas, 0, 0)

  canvas.width = tempCanvas.width
  canvas.height = tempCanvas.height
  ctx.drawImage(tempCanvas, 0, 0)

  return canvas
}

/**
 * Crops the image to specified rectangle
 */
export function cropImage(canvas, x, y, width, height) {
  const ctx = canvas.getContext('2d')
  const imageData = ctx.getImageData(x, y, width, height)

  canvas.width = width
  canvas.height = height
  ctx.putImageData(imageData, 0, 0)

  return canvas
}

/**
 * Helper to clamp values between 0-255
 */
function clamp(value) {
  return Math.max(0, Math.min(255, Math.round(value)))
}

/**
 * Loads an image from URL into a canvas
 * Uses fetch to avoid CORS issues with canvas tainted by cross-origin images
 */
export async function loadImageToCanvas(imageUrl) {
  console.log('[loadImageToCanvas] Starting to load:', imageUrl?.substring(0, 80))
  
  // For data URLs, load directly (no CORS issues)
  if (imageUrl?.startsWith('data:')) {
    console.log('[loadImageToCanvas] Loading data URL directly')
    return loadImageDirectly(imageUrl, false)
  }

  // For blob URLs, load directly
  if (imageUrl?.startsWith('blob:')) {
    console.log('[loadImageToCanvas] Loading blob URL directly')
    return loadImageDirectly(imageUrl, false)
  }
  
  // Method 1: Fetch as blob (bypasses CORS for canvas operations)
  try {
    console.log('[loadImageToCanvas] Trying fetch method...')
    const response = await fetch(imageUrl, {
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Accept': 'image/*'
      }
    })
    
    console.log('[loadImageToCanvas] Fetch response:', response.status, response.ok)
    
    if (response.ok) {
      const blob = await response.blob()
      console.log('[loadImageToCanvas] Got blob, size:', blob.size, 'type:', blob.type)
      
      const objectUrl = URL.createObjectURL(blob)
      console.log('[loadImageToCanvas] Created object URL:', objectUrl)
      
      try {
        const result = await loadImageDirectly(objectUrl, false)
        URL.revokeObjectURL(objectUrl)
        console.log('[loadImageToCanvas] Successfully loaded via fetch method')
        return result
      } catch (blobLoadError) {
        URL.revokeObjectURL(objectUrl)
        throw blobLoadError
      }
    } else {
      throw new Error(`Fetch failed with status ${response.status}`)
    }
  } catch (fetchError) {
    console.warn('[loadImageToCanvas] Fetch method failed:', fetchError.message)
  }

  // Method 2: Direct load with crossOrigin (fallback)
  console.log('[loadImageToCanvas] Trying direct load with crossOrigin...')
  try {
    return await loadImageDirectly(imageUrl, true)
  } catch (directError) {
    console.error('[loadImageToCanvas] Direct load also failed:', directError.message)
    throw new Error(
      'Cannot load image for editing. The image server needs CORS headers.\n\n' +
      'Workaround: Right-click the image, "Save Image As...", then upload the saved file here.'
    )
  }
}

/**
 * Helper to load image directly
 */
function loadImageDirectly(imageUrl, useCrossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    
    if (useCrossOrigin) {
      img.crossOrigin = 'anonymous'
    }
    
    img.onload = () => {
      console.log('[loadImageDirectly] Image loaded, size:', img.width, 'x', img.height)
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        // Use willReadFrequently for better performance with multiple getImageData calls
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(img, 0, 0)
        
        // Test if canvas is tainted by trying to read a pixel
        try {
          ctx.getImageData(0, 0, 1, 1)
          console.log('[loadImageDirectly] Canvas is not tainted, full access available')
        } catch (taintError) {
          console.warn('[loadImageDirectly] Canvas is tainted:', taintError.message)
          throw new Error('Canvas is tainted - CORS headers needed on server')
        }
        
        resolve({ canvas, ctx, img })
      } catch (canvasError) {
        reject(canvasError)
      }
    }
    
    img.onerror = (err) => {
      console.error('[loadImageDirectly] Image load error:', err)
      reject(new Error('Failed to load image'))
    }
    
    img.src = imageUrl
  })
}

/**
 * Converts canvas to blob
 */
export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality)
  })
}

/**
 * Converts canvas to data URL
 */
export function canvasToDataURL(canvas, type = 'image/jpeg', quality = 0.92) {
  return canvas.toDataURL(type, quality)
}

